import React from "react";
import {
  Box,
  Typography,
  Button,
  Modal,
  IconButton,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Stack
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import InvitationSuccessModal from "./InvitationSuccessModal";

interface AddOrganisationAdminModalProps {
  open: boolean;
  onClose: () => void;
  onAdminAdded?: (data: { adminUserName: string; email: string; mobileNumber: string }) => void;
}

const DESIGNATION_OPTIONS = [
  "Engineer",
  "Manager",
  "Director",
  "CEO",
  "CFO",
  "Other"
];

const AddOrganisationAdminModal: React.FC<AddOrganisationAdminModalProps> = ({
  open,
  onClose,
  onAdminAdded
}) => {
  const [form, setForm] = React.useState({
    adminUserName: "",
    designation: "",
    email: "",
    mobileNumber: ""
  });

  const [successOpen, setSuccessOpen] = React.useState(false);

  const handleChange = (field: string, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    // Handle send invitation logic here
    console.log("Send Invitation:", form);
    if (onAdminAdded) {
      onAdminAdded({
        adminUserName: form.adminUserName,
        email: form.email,
        mobileNumber: form.mobileNumber
      });
    }
    setSuccessOpen(true);
  };

  const handleClose = () => {
    setForm({
      adminUserName: "",
      designation: "",
      email: "",
      mobileNumber: ""
    });
    onClose();
  };

  return (
    <>
      <Modal open={open && !successOpen} onClose={handleClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90vw", sm: 500 },
            bgcolor: "#fff",
            borderRadius: 3,
            p: { xs: 3, sm: 4 },
            outline: "none"
          }}
        >
          {/* Header */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            mb={0.5}
          >
            <Typography
              sx={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 700,
                fontSize: { xs: 18, sm: 20 },
                color: "#212121"
              }}
            >
              Add Organisation Admin
            </Typography>
            <IconButton
              onClick={handleClose}
              size="small"
              sx={{ color: "#212121", mt: -0.5, mr: -1 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>

          <Typography
            sx={{
              fontFamily: "Inter, sans-serif",
              fontSize: 13,
              color: "#757575",
              mb: 3
            }}
          >
            Enter required details to complete your registration
          </Typography>

          {/* Form Fields */}
          <Stack spacing={2.5}>
            <TextField
              fullWidth
              label="Admin User Name"
              value={form.adminUserName}
              onChange={e => handleChange("adminUserName", e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={fieldSx}
            />

            <FormControl fullWidth>
              <InputLabel shrink sx={labelSx}>
                Designation
              </InputLabel>
              <Select
                value={form.designation}
                onChange={e => handleChange("designation", e.target.value)}
                displayEmpty
                notched
                label="Designation"
                IconComponent={KeyboardArrowDownIcon}
                sx={fieldSx}
              >
                <MenuItem value="" disabled>
                  <span style={{ color: "#9CA3AF" }}>Select designation</span>
                </MenuItem>
                {DESIGNATION_OPTIONS.map(opt => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="E-mail"
              type="email"
              value={form.email}
              onChange={e => handleChange("email", e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={fieldSx}
            />

            <TextField
              fullWidth
              label="Mobile Number"
              value={form.mobileNumber}
              onChange={e => {
                const val = e.target.value;
                if (/^\d{0,10}$/.test(val)) handleChange("mobileNumber", val);
              }}
              InputLabelProps={{ shrink: true }}
              sx={fieldSx}
            />
          </Stack>

          {/* Send Invitation Button */}
          <Button
            fullWidth
            onClick={handleSubmit}
            sx={{
              mt: 4,
              bgcolor: "#3A1C73",
              color: "#fff",
              fontFamily: "Inter, sans-serif",
              fontWeight: 600,
              fontSize: 16,
              textTransform: "none",
              borderRadius: 999,
              py: 1.5,
              "&:hover": {
                bgcolor: "#2e1560"
              }
            }}
          >
            Send Invitation
          </Button>
        </Box>
      </Modal>

      {/* Success Modal */}
      <InvitationSuccessModal
        open={successOpen}
        onClose={() => {
          setSuccessOpen(false);
          handleClose();
        }}
        email={form.email}
      />
    </>
  );
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    fontFamily: "Inter, sans-serif",
    fontSize: 15,
    "& fieldset": { borderColor: "#D1D5DB" },
    "&:hover fieldset": { borderColor: "#9CA3AF" },
    "&.Mui-focused fieldset": { borderColor: "#3A1C73" }
  },
  "& .MuiInputLabel-root": {
    fontFamily: "Inter, sans-serif",
    fontSize: 13,
    color: "#9CA3AF",
    "&.Mui-focused": { color: "#3A1C73" }
  }
};

const labelSx = {
  fontFamily: "Inter, sans-serif",
  fontSize: 13,
  color: "#9CA3AF",
  "&.Mui-focused": { color: "#3A1C73" }
};

export default AddOrganisationAdminModal;
